import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AddDevice = () => {
  const { accessToken } = useAuth();
  const location = useLocation();
  const { siteId } = location.state || {};
  const baseURL = process.env.REACT_APP_BASEURL;
  const mapRef = useRef(null);

  // State
  const [serialNumber, setSerialNumber] = useState("");
  const [name, setName] = useState("");
  const [soilType, setSoilType] = useState("loam");
  const [soilDryBulkDensity, setSoilDryBulkDensity] = useState("1.4");
  const [fieldCapacity, setFieldCapacity] = useState("25");
  const [wiltingPoint, setWiltingPoint] = useState("8");
  const [irrigationThreshold, setIrrigationThreshold] = useState("0.5");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [depth, setDepth] = useState("");
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [groups, setGroups] = useState([]);
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [isValidCoordinates, setIsValidCoordinates] = useState(true);
  const [mapEnabled, setMapEnabled] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Satellite tile layer
  const tileLayer = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  };

  // Fetch sites and user data, set default coordinates
  useEffect(() => {
    const fetchSitesAndUser = async () => {
      setLoadingSites(true);
      setError(null);
      try {
        // Fetch sites
        const siteRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteList = siteRes.data.data || [];

        // Fetch user data
        const userRes = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0] || {};
        const userSiteIds = userData.sites || [];

        // Filter user-assigned sites
        const userSites = siteList.filter((site) =>
          userSiteIds.includes(site.id)
        );
        setSites(userSites);

        // Set initial site
        const initialSite = userSites.some((site) => site.id === siteId)
          ? siteId
          : userSites.length > 0
          ? userSites[0].id
          : "";
        setSelectedSite(initialSite);

        // Fetch site details for coordinates
        if (initialSite) {
          const siteDetailsRes = await axios.get(
            `${baseURL}/sites/${initialSite}/?with_image=true`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          const { latitude, longitude } = siteDetailsRes.data || {};
          if (latitude && longitude) {
            setLatitude(latitude.toString());
            setLongitude(longitude.toString());
            setMapCenter([latitude, longitude]);
          }
        }
      } catch (err) {
        setError("Failed to load sites. Please try again.");
        console.error("Sites fetch error:", err.response ? err.response.data : err.message);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSitesAndUser();
  }, [accessToken, baseURL, siteId]);

  // Fetch groups when selectedSite changes
  useEffect(() => {
    const fetchGroups = async () => {
      if (!selectedSite) {
        setGroups([]);
        setSelectedGroup([]);
        return;
      }
      setLoadingGroups(true);
      setError(null);
      try {
        const response = await axios.get(`${baseURL}/groups/?site=${selectedSite}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setGroups(response.data || []);
        setSelectedGroup([]); // Reset group selection
      } catch (err) {
        setError("Failed to load groups. Please try again.");
        console.error("Groups fetch error:", err.response ? err.response.data : err.message);
      } finally {
        setLoadingGroups(false);
      }
    };

    // Update coordinates when site changes
    const fetchSiteCoordinates = async () => {
      if (!selectedSite) {
        setLatitude("");
        setLongitude("");
        setMapCenter([0, 0]);
        return;
      }
      try {
        const response = await axios.get(
          `${baseURL}/sites/${selectedSite}/?with_image=true`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const { latitude, longitude } = response.data || {};
        if (latitude && longitude) {
          setLatitude(latitude.toString());
          setLongitude(longitude.toString());
          setMapCenter([latitude, longitude]);
        }
      } catch (err) {
        console.error("Site details fetch error:", err.response ? err.response.data : err.message);
      }
    };

    fetchGroups();
    fetchSiteCoordinates();
  }, [accessToken, baseURL, selectedSite]);

  // Validate coordinates
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180;
    setIsValidCoordinates(isValidLat && isValidLon);
    if (isValidLat && isValidLon) {
      setMapCenter([lat, lon]);
    }
  }, [latitude, longitude]);

  // Map component to update coordinates on click
  const MapUpdater = ({ center, onClick }) => {
    const map = useMap();
    useEffect(() => {
      if (center[0] !== 0 && center[1] !== 0 && mapEnabled) {
        map.setView(center, 15);
      }
    }, [center, map, mapEnabled]);

    useEffect(() => {
      const handleClick = (e) => {
        const { lat, lng } = e.latlng;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        onClick([lat, lng]);
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }, [map, onClick]);

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoadingSubmit(true);

    // Validate inputs
    if (!serialNumber.trim()) {
      setError("Serial number is required.");
      setLoadingSubmit(false);
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      setLoadingSubmit(false);
      return;
    }
    if (!latitude || !longitude || !isValidCoordinates) {
      setError("Valid latitude and longitude are required.");
      setLoadingSubmit(false);
      return;
    }
    if (!selectedSite) {
      setError("A site must be selected.");
      setLoadingSubmit(false);
      return;
    }

    try {
      const parsedSerialNumber = Number(serialNumber);
      const parsedSite = Number(selectedSite);
      const parsedGroups =
        selectedGroup.length > 0 ? selectedGroup.map((groupId) => Number(groupId)) : [];

      const deviceData = {
        serial_number: parsedSerialNumber,
        name,
        device_type: "hydra",
        location: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          height: depth ? parseFloat(depth) / -100 : null,
          soil_type: soilType,
          soil_density: parseFloat(soilDryBulkDensity),
          field_capacity: parseFloat(fieldCapacity),
          wilting_point: parseFloat(wiltingPoint),
          irrigation_threshold: parseFloat(irrigationThreshold),
        },
        site: parsedSite,
        groups: parsedGroups,
      };
      await axios.post(`${baseURL}/devices/`, deviceData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSuccess("Device added successfully!");
      // Reset form
      setSerialNumber("");
      setName("");
      setSoilType("loam");
      setSoilDryBulkDensity("1.4");
      setFieldCapacity("25");
      setWiltingPoint("8");
      setIrrigationThreshold("0.5");
      setLatitude(sites.find((s) => s.id === selectedSite)?.latitude?.toString() || "");
      setLongitude(sites.find((s) => s.id === selectedSite)?.longitude?.toString() || "");
      setDepth("");
      setSelectedGroup([]);
      setMapEnabled(false);
    } catch (err) {
      setError("Failed to add device. Please try again.");
      console.error("Device creation error:", err.response ? err.response.data : err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle site selection
  const handleSiteChange = (e) => {
    setSelectedSite(e.target.value);
  };

  // Get display names
  const getSiteDisplayName = (site) => {
    return site.name || `Site ${site.id}`;
  };

  const getGroupDisplayName = (group) => {
    return group.name || `Group ${group.id}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
          Add New Device
        </Typography>
        <FormControl
          variant="outlined"
          sx={{ minWidth: { xs: "100%", sm: 200 }, width: { xs: "100%", sm: "auto" } }}
          disabled={loadingSites}
        >
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select
            labelId="site-select-label"
            value={selectedSite}
            onChange={handleSiteChange}
            label="Site"
          >
            {loadingSites ? (
              <MenuItem value="" disabled>
                Loading sites...
              </MenuItem>
            ) : sites.length === 0 ? (
              <MenuItem value="" disabled>
                No sites available
              </MenuItem>
            ) : (
              sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {getSiteDisplayName(site)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Serial Number and Name */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Serial Number"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
          </Box>

          {/* Soil Spec */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Soil Specifications
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="soil-type-select-label">Soil Type</InputLabel>
              <Select
                labelId="soil-type-select-label"
                value={soilType}
                onChange={(e) => setSoilType(e.target.value)}
                label="Soil Type"
              >
                {[
                  { value: "loam", label: "Loamy" },
                  { value: "clay", label: "Clay" },
                  { value: "usga", label: "USGA" },
                  { value: "organic", label: "Organic" },
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Soil Dry Bulk Density (kg/l)"
              value={soilDryBulkDensity}
              onChange={(e) => setSoilDryBulkDensity(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: {
                    step: 0.2,
                    min: 0,
                  }
                }
              }}
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Field Capacity (%)"
              value={fieldCapacity}
              onChange={(e) => setFieldCapacity(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: {
                    step: 1,
                    min: 0,
                  }
                }
              }}
              variant="outlined"
            />
            <TextField
              label="Wilting Point (%)"
              value={wiltingPoint}
              onChange={(e) => setWiltingPoint(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: {
                    step: 1,
                    min: 0,
                  }
                }
              }}
              variant="outlined"
            />
            <TextField
              label="Irrigation Threshold"
              value={irrigationThreshold}
              onChange={(e) => setIrrigationThreshold(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: {
                    step: 0.01,
                    min: 0,
                  }
                }
              }}
              variant="outlined"
            />
          </Box>

          {/* Location */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Location
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && latitude !== ""}
              helperText={
                !isValidCoordinates && latitude !== ""
                  ? "Latitude must be between -90 and 90"
                  : ""
              }
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && longitude !== ""}
              helperText={
                !isValidCoordinates && longitude !== ""
                  ? "Longitude must be between -180 and 180"
                  : ""
              }
            />
          </Box>
          <Box sx={{ height: 300, border: "1px solid #ddd", mb: 2 }}>
            {mapEnabled && isValidCoordinates && latitude && longitude ? (
              <div ref={mapRef} style={{ height: "100%", width: "100%" }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
                  <TileLayer
                    url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution=""
                  />
                  <Marker position={mapCenter} />
                  <MapUpdater center={mapCenter} onClick={setMapCenter} />
                </MapContainer>
              </div>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  cursor: "pointer",
                }}
                onClick={() => setMapEnabled(true)}
              >
                <Typography color="text.secondary">
                  {latitude && longitude ? "Click the map to set coordinates" : "Click to enable map"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Depth */}
          <TextField
            label="Depth (cm)"
            value={depth}
            onChange={(e) => setDepth(e.target.value)}
            fullWidth
            type="number"
            slotProps={{
              input: {
                inputProps: {
                  step: 1,
                  min: 0,
                }
              }
            }}
            variant="outlined"
          />

          {/* Groups Dropdown */}
          <FormControl fullWidth variant="outlined" disabled={loadingGroups || !selectedSite}>
            <InputLabel id="group-select-label">Group (optional)</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              label="Group (optional)"
              multiple
              renderValue={(selected) =>
                selected
                  .map((groupId) => {
                    const group = groups.find((g) => g.id === groupId);
                    return group ? getGroupDisplayName(group) : `Group ${groupId}`;
                  })
                  .join(", ")
              }
            >
              {loadingGroups ? (
                <MenuItem value="" disabled>
                  Loading groups...
                </MenuItem>
              ) : groups.length === 0 ? (
                <MenuItem value="" disabled>
                  No groups available
                </MenuItem>
              ) : (
                groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {getGroupDisplayName(group)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Save Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loadingSubmit || loadingSites || loadingGroups || !selectedSite}
            sx={{ mt: 2, alignSelf: "flex-end" }}
          >
            {loadingSubmit ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddDevice;
